import { useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { Button, Grid } from '@mui/material';
import { v4 as uuid } from 'uuid';
import useCommonStyles from '../../../../../../commonStyles';
import InputConfig from '../../../../../../Components/InputConfig';
import { initialContentMappingObj } from '../../../../../../utilities/initialConfigs';
import DropDownSingle from '../../../../../../Components/DropDownSingle';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { createPlaylistMapping } from '../../../../../../api/contentMapping';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import { CONTENT_MAPPING } from '../../../../../../core/constants';
import CheckBoxInput from '../../../../../../Components/CheckBoxInput';

const CreatePlaylistModal = () => {
	const commonStyleClasses = useCommonStyles();
	const { updateModalContent } = useContext(Modalv2Context);
	const { siteRawConfig, categories } = useContext(UnitsContext);
	const [newVideoMappingData, setNewVideoMappingData] = useState(initialContentMappingObj);
	const [triggerProcessing, setTriggerProcessing] = useState(false);

	/**
	 * Function for Validating Mapping Data Inputs
	 */
	const validateMappingFormData = () => {
		const { mode, playlistUrl, channelId, playlistName, category } = newVideoMappingData;
		const isPlaylistMode = mode === 'MAP_YT_PLAYLIST';
		const isFieldMissing = isPlaylistMode ? !playlistUrl : !channelId;

		if (isFieldMissing || !playlistName || !category) {
			throw new Error('Please Fill in All Fields');
		}

		if (/\s/g.test(playlistName)) {
			throw new Error(`${playlistName} should not contain any whitespace character`);
		}
	};

	/**
	 * Function to Trigger New Mapping for provided YouTube Channel Details
	 */
	const createNewMapping = async () => {
		try {
			validateMappingFormData();
			setTriggerProcessing(true);

			const payload = {
				data: {
					...newVideoMappingData,
					activeAudioVersion: newVideoMappingData.uploadMuted === true ? 'muted' : 'unmuted',
					playlistId: uuid().toString(),
					ownerEmails: [siteRawConfig.ownerEmail]
				}
			};

			const response = await createPlaylistMapping(payload);
			toast.success(response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
			setTriggerProcessing(false);
			updateModalContent(null);
		} catch (error) {
			const errMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errMessage || 'Some error occurred', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			setTriggerProcessing(false);
		}
	};

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			width="fit-content !important"
			height="fit-content !important"
		>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<DropDownSingle
					isObjectEditor={false}
					directValue={newVideoMappingData.mode}
					updateDirectValue={(value) => {
						setNewVideoMappingData({ ...newVideoMappingData, mode: value });
					}}
					list={CONTENT_MAPPING.YT_MAPPING_MODES}
					label="Mapping Mode"
					labelStyles={{ width: '100%' }}
					formControlStyles={{ width: '100%' }}
					formControlVariant={'outlined'}
					selectInputStyles={{ margin: '1rem 0' }}
				/>
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<DropDownSingle
					isObjectEditor={false}
					directValue={newVideoMappingData.category}
					updateDirectValue={(value) => {
						setNewVideoMappingData({ ...newVideoMappingData, category: value });
					}}
					list={categories}
					label="Select Category"
					labelStyles={{ width: '100%' }}
					formControlStyles={{ width: '100%' }}
					formControlVariant={'outlined'}
					selectInputStyles={{ margin: '1rem 0' }}
				/>
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				{newVideoMappingData.mode === 'MAP_YT_PLAYLIST' && (
					<InputConfig
						label="Playlist URL"
						inputType="string"
						containerStyles={{ display: 'block' }}
						labelStyles={{ width: '100%' }}
						isObjectEditor={false}
						inputStyles={{ width: '100%', margin: '1rem 0' }}
						placeholder="https://www.youtube.com/playlist?list=PLXJ0on0Dau77THnZeQLqK1hV6s2udsaAm"
						directValue={newVideoMappingData.playlistUrl}
						updateDirectValue={(value) => {
							setNewVideoMappingData({ ...newVideoMappingData, playlistUrl: value });
						}}
					/>
				)}
				{newVideoMappingData.mode === 'MAP_YT_LATEST_VIDEO' && (
					<InputConfig
						label="YouTube Channel ID"
						inputType="string"
						containerStyles={{ display: 'block' }}
						labelStyles={{ width: '100%' }}
						isObjectEditor={false}
						inputStyles={{ width: '100%', margin: '1rem 0' }}
						placeholder="UCTCDjn7rnejAvhOrhlVgZrw"
						directValue={newVideoMappingData.channelId}
						updateDirectValue={(value) => {
							setNewVideoMappingData({ ...newVideoMappingData, channelId: value });
						}}
					/>
				)}
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<InputConfig
					label="Video Mapping Name"
					inputType="string"
					containerStyles={{ display: 'block' }}
					labelStyles={{ width: '100%' }}
					isObjectEditor={false}
					inputStyles={{ width: '100%', margin: '1rem 0' }}
					placeholder="Tech Playlist - 12345"
					directValue={newVideoMappingData.playlistName}
					updateDirectValue={(value) => {
						setNewVideoMappingData({ ...newVideoMappingData, playlistName: value });
					}}
				/>
			</Grid>
			<Grid item xs={6} className={commonStyleClasses.modalContentItem}>
				<CheckBoxInput
					label="Muted Playlist"
					disabled={false}
					defaultChecked={false}
					isObjectEditor={false}
					directValue={newVideoMappingData.uploadMuted}
					updateDirectValue={(value) => {
						setNewVideoMappingData({
							...newVideoMappingData,
							uploadMuted: !newVideoMappingData.uploadMuted
						});
					}}
					style={{ width: '100%', marginLeft: '0' }}
				/>
			</Grid>
			<Grid
				item
				xs={6}
				className={commonStyleClasses.modalContentItem}
				style={{ justifyContent: 'flex-end' }}
			>
				<Button
					disabled={triggerProcessing}
					variant="contained"
					sx={{ maxHeight: 'fit-content', margin: 'auto 0' }}
					onClick={() => {
						createNewMapping();
					}}
				>
					Create New Mapping
				</Button>
			</Grid>
		</Grid>
	);
};

export default CreatePlaylistModal;
